import { accountEn } from './account-en.ts';
import { adminEn } from './admin-en.ts';
import { gameEn } from './game-en.ts';
import { metaEn } from './meta-en.ts';
import { socialEn } from './social-en.ts';
import { onlineEn } from './online-en.ts';
import { pwaEn } from './pwa-en.ts';

/**
 * English strings. This file defines the shape of every translation: `it.ts` is typed
 * as `typeof en`, so a missing or extra key is a compile error.
 */
export const en = {
  common: {
    appName: 'Il Colpo',
    loading: 'Loading',
    back: 'Back',
    close: 'Close',
    confirm: 'Confirm',
    cancel: 'Cancel',
    on: 'On',
    off: 'Off',
    soon: 'Coming soon',
    yes: 'Yes',
    no: 'No',
    reset: 'Reset',
    version: 'Version {{version}}',
    skipToContent: 'Skip to content',
    languages: { it: 'Italiano', en: 'English' },
  },
  splash: {
    tagline: 'One last job.',
    tapToStart: 'Tap to start',
    steps: {
      fonts: 'Setting the type',
      graphics: 'Checking the lights',
      language: 'Reading the room',
      ready: 'Ready',
    },
    updateRequired: 'Update required',
    updateBody: 'This version is too old to play online. Please update the app.',
  },
  firstRun: {
    title: 'Welcome, partner',
    subtitle: 'Pick your language. You can change it later in Settings.',
    continue: 'Continue',
  },
  home: {
    subtitle: 'A heist for three to five. Trust is optional.',
    menu: {
      play: 'Play',
      playHint: 'Solo, training, private rooms',
      online: 'Online',
      onlineHint: 'Quick match, ranked, friends',
      profile: 'Profile',
      profileHint: 'Your record',
      collection: 'Collection',
      collectionHint: 'Outfits and blueprints',
      leaderboards: 'Leaderboards',
      leaderboardsHint: 'Global, friends, season',
      settings: 'Settings',
      settingsHint: 'Sound, graphics, controls',
      news: 'News',
      newsHint: 'Events and updates',
    },
    banner: {
      open: 'Play',
      title: 'Heist of the Day',
      body: 'One seed, everyone plays it. Coming soon.',
    },
  },
  play: {
    title: 'Play',
    modes: {
      quick: { name: 'Quick match', desc: '3–5 players online. Bots fill empty seats.' },
      ranked: { name: 'Ranked', desc: '4–5 players. Seasons and leagues.' },
      private: { name: 'Private room', desc: 'Share a code, a link or a QR with friends.' },
      solo: { name: 'Solo', desc: 'You and 2–4 bots. Can the Mole be unmasked?' },
      training: { name: 'Training', desc: 'Every puzzle on its own, against the clock.' },
      daily: { name: 'Heist of the Day', desc: 'Same seed for everyone, with bots.' },
      tutorial: { name: 'Tutorial', desc: 'A guided heist, one role at a time.' },
    },
  },
  comingSoon: {
    title: 'Not open yet',
    body: 'This part of the game is being built. It will open in a later update.',
  },
  notFound: {
    title: 'Wrong door',
    body: 'There is nothing behind this one.',
    home: 'Back to the hideout',
  },
  settings: {
    title: 'Settings',
    tabs: {
      audio: 'Audio',
      graphics: 'Graphics',
      game: 'Game',
      accessibility: 'Accessibility',
      controls: 'Controls',
      language: 'Language',
      notifications: 'Notifications',
      account: 'Account & privacy',
    },
    audio: {
      master: 'Master volume',
      music: 'Music',
      sfx: 'Sound effects',
      note: 'The music and the effects are made by the game itself, as it plays. There is nothing to download.',
      test: 'Play a sample',
    },
    graphics: {
      preset: 'Quality preset',
      presets: {
        auto: 'Auto',
        low: 'Low',
        medium: 'Medium',
        high: 'High',
        ultra: 'Ultra',
      },
      detected: 'Detected for this device: {{preset}}',
      fpsLimit: 'Frame rate limit',
      batterySaver: 'Battery saver',
      batterySaverHint: 'Caps at 30 fps and turns off heavy effects.',
      effects: 'Effects',
      bloom: 'Bloom',
      grain: 'Film grain',
      chromaticAberration: 'Chromatic aberration',
      volumetricLight: 'Volumetric light',
      rain: 'Rain',
      particles: 'Particles',
    },
    game: {
      confirmations: 'Confirm risky actions',
      confirmationsHint: 'Ask before leaving a match or burning a Plan B.',
      hints: 'Show hints',
      animationSpeed: 'Animation speed',
      animationSpeedValue: '{{value}}×',
    },
    accessibility: {
      colorblind: 'Colour-blind mode',
      colorblindHint: 'Adds symbols and patterns to everything that uses colour.',
      largeText: 'Large text',
      highContrast: 'High contrast',
      reduceMotion: 'Reduce motion',
      reduceMotionHint: 'No screen shake, no flashes, calmer animation.',
      reduceMotionSystem: 'Follow system',
      vibration: 'Vibration',
      leftHanded: 'Left-handed layout',
      uiScale: 'Interface size',
    },
    controls: {
      hint: 'Pick an action, then press the key you want. Keyboard only; touch controls are fixed.',
      pressKey: 'Press a key…',
      conflict: '"{{key}}" is already used by {{action}}.',
      resetAll: 'Restore default keys',
      actions: {
        quickPhrase1: 'Quick phrase 1',
        quickPhrase2: 'Quick phrase 2',
        quickPhrase3: 'Quick phrase 3',
        quickPhrase4: 'Quick phrase 4',
        quickPhrase5: 'Quick phrase 5',
        quickPhrase6: 'Quick phrase 6',
        quickPhrase7: 'Quick phrase 7',
        quickPhrase8: 'Quick phrase 8',
        quickPhrase9: 'Quick phrase 9',
        ping: 'Ping',
        toggleBlueprint: 'Show blueprint',
        toggleChat: 'Open chat',
        pause: 'Pause / menu',
      },
    },
    language: { title: 'Game language' },
    notifications: {
      invites: 'Invitations',
      friends: 'Friend requests',
      events: 'Events',
      season: 'Season end',
      note: 'Push notifications arrive with online play.',
    },
    account: {
      note: 'Accounts are not available in this version. The game is complete without one.',
      open: 'Open account',
      openHint: 'Profile, cloud save, privacy and your data.',
    },
    footer: { gallery: 'Component gallery' },
  },
  gallery: {
    title: 'Component gallery',
    intro: 'Every component in every state. Development tool.',
    buttons: 'Buttons',
    controls: 'Controls',
    feedback: 'Feedback',
    typography: 'Typography',
    palette: 'Palette',
    primary: 'Primary',
    secondary: 'Secondary',
    ghost: 'Ghost',
    danger: 'Danger',
    disabled: 'Disabled',
    loadingState: 'Loading',
    selected: 'Selected',
    sampleSwitch: 'Sample switch',
    sampleSlider: 'Sample slider',
    sampleSegmented: 'Sample choice',
    optionA: 'One',
    optionB: 'Two',
    optionC: 'Three',
    openModal: 'Open dialog',
    modalTitle: 'Burn a Plan B?',
    modalBody: 'You will undo the last mistake and its alarm. You only have two.',
    showToast: 'Show toast',
    toastText: 'Plan approved.',
    tooltipTarget: 'Hold or hover',
    tooltipText: 'Long press on touch, hover or focus on desktop.',
    progress: 'Progress',
    alarm: 'Alarm',
    display: 'Display type',
    interface: 'Interface type',
    typewriter: 'Typewriter notes',
    sampleText: 'The vault opens at midnight.',
    badge: 'Badge',
  },
  ...gameEn,
  ...accountEn,
  ...onlineEn,
  ...metaEn,
  ...socialEn,
  ...adminEn,
  ...pwaEn,
};

type DeepString<T> = T extends string ? string : { [K in keyof T]: DeepString<T[K]> };

/** Shape every language must implement (same keys, string values). */
export type Translation = DeepString<typeof en>;
